import { Businesses, Routes, Users } from "@just-in-types";
import { isEqual } from "lodash";
import { AnyAction, Dispatch } from "redux";
import store from "../index";
import { IState } from "../reducers";
import { IRouterState } from "../reducers/routerReducer";
import { emptyUser } from "./userActions";

export const isRouteString = (value: string) =>
  value === "coupons" ||
  value === "covidquestions" ||
  value === "dateofbirth" ||
  value === "email" ||
  value === "language" ||
  value === "mobile" ||
  value === "name" ||
  value === "signature" ||
  value === "supercard" ||
  value === "thanks" ||
  value === "vehicles" ||
  value === "zip" ||
  value === "waiver" ||
  value === "mugshot";

let indexIterator: null | IterableIterator<number> = null;
let routePaths: Routes.RouteString[] = [];

/* 
  Need to check signature field differently because some
  yards want a picture and some don't.

  TODO: Move mugshot into its own route.
*/
const signatureFieldIsEmpty = (user: Users.User) => {
  const { signature } = store.getState().router.routes;

  return signature.takeMugshot && user.mugshot === null;
};

const userFieldIsEmpty = (user: Users.User, path: Routes.RouteString): boolean => {
  if (path === "signature") {
    return signatureFieldIsEmpty(user);
  }

  return isEqual(user[path], emptyUser[path]);
};

function* indexGenerator() {
  let i = 0;

  while (i < routePaths.length) {
    yield i++;
  }
}

function* routePathGenerator(): IterableIterator<Routes.RouteString> {
  const {
    router: { routes },
    user,
  }: IState = store.getState();
  const index = indexIterator.next().value;
  const path = routePaths[index];
  const route = routes ? routes[path] : null;

  if (path) {
    if (route && route.enabled && (route.alwaysShow || userFieldIsEmpty(user, path))) {
      yield path;
    } else {
      yield routePathGenerator().next().value;
    }
  }
}

export const NAVIGATE_TO_NEXT = "router.NAVIGATE_TO_NEXT";
export const navigateToNext: NavigateToNext = () => (dispatch: Dispatch) => {
  const {
    router: { navigate, rootPath, routes },
    user: { skipCheckIn, mobile },
    user,
  }: IState = store.getState();
  const nextPath = (path) => `${rootPath}/${path}`;
  const navigateToThanks = () => {
    navigate(nextPath`thanks`);

    return dispatch({ type: NAVIGATE_TO_NEXT, payload: nextPath`thanks` });
  };

  if (!indexIterator) {
    // Iterator needs to be reset when it's done.
    indexIterator = indexGenerator();
  }

  if (skipCheckIn && mobile !== "6028181729") {
    // reset the indexIterator as we are skipping the rest of the routes.
    // if this isn't reset, the next person to chekc in will resume where the previous check in left off

    // TODO if a business starts to use the optin route: implement this, need to check that user has previously not opted in,
    // console.log(routes);
    // if(routes.optin){
    //   navigate(nextPath`optin`);
    //   return dispatch({ type: NAVIGATE_TO_NEXT, payload: nextPath`optin` });
    // }
    indexIterator = indexGenerator();
    return navigateToThanks();
  }

  if (routes) {
    routePaths = Object.keys(routes) as Routes.RouteString[];

    const { done, value } = routePathGenerator().next();

    if (!done && value) {
      const nextPathString = nextPath(value);

      navigate(nextPathString);

      return dispatch({
        payload: nextPathString,
        type: NAVIGATE_TO_NEXT,
      });
    } else {
      indexIterator = null;

      return navigateToThanks();
    }
  } else {
    return navigateToThanks();
  }
};

export const SET_SHOW_COUPONS = "businesses.SET_SHOW_COUPONS";
export const setShowCoupons = (showCoupons: boolean) => (dispatch: Dispatch<AnyAction>) =>
  dispatch({ payload: showCoupons, type: SET_SHOW_COUPONS });

export const SET_LANGUAGE = "user.SET_LANGUAGE";
export const setLanguage = (language: Users.Language) => (dispatch: Dispatch) =>
  dispatch({ payload: language, type: SET_LANGUAGE });

export const SET_BUSINESS_PATH = "router.SET_BUSINESS_PATH";
export const setBusinessPath = (path: string) => (dispatch: Dispatch) =>
  dispatch({ payload: path, type: SET_BUSINESS_PATH });

export const SET_ROOT_PATH = "router.SET_ROOT_PATH";
export const setRootPath = (path: string) => (dispatch: Dispatch) => dispatch({ payload: path, type: SET_ROOT_PATH });

export const SET_ROUTES_OBJECT = "router.SET_ROUTES_OBJECT";
export const SET_ROUTES_STRINGS = "router.SET_ROUTES_STRINGS";
export const setRouteComponents =
  (businessId: string, routes: Routes.RoutesObject | Routes.RouteString[]) => (dispatch: Dispatch) => {
    if (businessId) {
      if (routes) {
        const keystrings = Object.keys(routes);
        keystrings.sort((x, y) => (x === "signature" ? -1 : y === "signature" ? 1 : 0));
        dispatch({ payload: routes, type: SET_ROUTES_STRINGS });
      }

      const map = new Map(Object.entries(routes));
      const order = [
        // 'language',
        "covidquestions",
        "name",
        "zip",
        "dateofbirth",
        "vehicles",
        "signature",
        "customertype",
        "email",
        // "dateofbirth",
        "receiptCode",
        "supercard",
        "optin",
      ];
      const sortedMap = new Map();
      for (const i in order) {
        if (map.get(order[i])) {
          sortedMap.set(order[i], map.get(order[i]));
        }
      }
      const sortedObj = {};
      sortedMap.forEach((value, key) => {
        sortedObj[key] = value;
      });

      return dispatch({
        payload: sortedObj as Routes.RoutesObject,
        type: SET_ROUTES_OBJECT,
      });
    } else {
      if (routes) {
        dispatch({ payload: Object.keys(routes), type: SET_ROUTES_STRINGS });
      }

      return dispatch({ payload: routes, type: SET_ROUTES_OBJECT });
    }
  };
export type SetAllowOptOut = (allow: boolean) => (dispatch: Dispatch<AnyAction>) => {
  payload: boolean;
  type: string;
};
export const SET_ALLOW_OPT_OUT = "router.SET_ALLOW_OPT_OUT";
export const setAllowOptOut = (allow: boolean) => (dispatch: Dispatch) =>
  dispatch({ payload: allow, type: SET_ALLOW_OPT_OUT });

export const SET_BUSINESS_ID = "router.SET_BUSINESS_ID";
export const setBusinessId =
  ({ businessId }: { businessId: string }) =>
  (dispatch: Dispatch) =>
    dispatch({ payload: { businessId }, type: SET_BUSINESS_ID });

export const SET_GUID = "router.SET_GUID";
export const setGuid =
  ({ guid }: { guid: string }) =>
  (dispatch: Dispatch) =>
    dispatch({ payload: { guid }, type: SET_GUID });

export const SET_ACTIVE_SITE = "router.SET_ACTIVE_SITE";
export const setActiveSite = (site: Businesses.Site) => (dispatch: Dispatch<AnyAction>) => {
  const { path } = site;
  const capitalized = path.charAt(0).toUpperCase() + path.slice(1);

  dispatch({
    payload: { nicePath: capitalized, ...site },
    type: SET_ACTIVE_SITE,
  });
};

export const SET_ACTIVE_BUSINESS = "router.SET_ACTIVE_BUSINESS";
export const setActiveBusiness = (business: any) => (dispatch: Dispatch) =>
  dispatch({ payload: business, type: SET_ACTIVE_BUSINESS });

export const SET_OPT_IN_FIELD = "router.SET_OPT_IN_FIELD";
export const setOptInField = (optInField: boolean) => (dispatch: Dispatch<AnyAction>) => {
  dispatch({ payload: optInField, type: SET_OPT_IN_FIELD });
};

export const RESET_AFTER_ERROR = "router.RESET_AFTER_ERROR";
export const resetAfterError = () => {
  const { navigate, businessPath } = store.getState().router as IRouterState;

  if (businessPath) {
    navigate(businessPath);
  }
};

export type SetShowCoupons = (showCoupons: boolean) => (dispatch: Dispatch<AnyAction>) => {
  payload: boolean;
  type: string;
};
export type SetLanguage = (language: Users.Language) => (dispatch: Dispatch<AnyAction>) => {
  type: string;
  payload: Users.Language;
};
export type SetBusinessPath = (path: string) => (dispatch: Dispatch<AnyAction>) => {
  type: string;
  payload: string;
};
export type SetRootPath = (path: string) => (dispatch: Dispatch<AnyAction>) => {
  type: string;
  payload: string;
};
export type SetRouteComponents = (
  businessId: string,
  routes: Routes.RoutesObject | Routes.RouteString[]
) => (dispatch: Dispatch<AnyAction>) => {
  type: string;
  payload: Routes.RoutesObject | Routes.RouteString[];
};
export type SetBusinessId = ({ businessId }: { businessId: string }) => (dispatch: Dispatch<AnyAction>) => {
  payload: {
    businessId: string;
  };
  type: string;
};
export type SetGuid = ({ guid }: { guid: string }) => (dispatch: Dispatch<AnyAction>) => {
  payload: {
    guid: string;
  };
  type: string;
};
export type NavigateToNext = () => (dispatch: Dispatch<AnyAction>) => {
  type: string;
  payload: string;
};

export type SetActiveBusiness = (business: any) => (dispatch: Dispatch<AnyAction>) => void;

export type SetActiveSite = (site: Businesses.Site) => (dispatch: Dispatch<AnyAction>) => void;



// WEBPACK FOOTER //
// ./src/store/actions/routerActions.ts